import { useCallback, useMemo } from 'react';

import { HandleType, useWatch } from '@kanda-libs/ks-component-ts';
import { ButtonTextProps, useIsDesktop } from '@kanda-libs/ks-design-library';

import { BUTTON_PROPS, FIELD_TEXT, TICKBOX_PROPS } from './constants';

import { useOmniSignDocumentsContext } from '../../../context';
import { BasePage } from '../constants';

interface TickboxProps {
  handle: HandleType;
  name: string;
  fieldText: string;
}

interface OmniSignDocumentsFormBasePageSignDocumentHook {
  tickboxProps: TickboxProps;
  buttonProps: ButtonTextProps;
}

export default function useOmniSignDocumentsFormBasePageSignDocument(
  page: BasePage,
  scrollRef: React.MutableRefObject<HTMLDivElement | null>,
): OmniSignDocumentsFormBasePageSignDocumentHook {
  const { setPage } = useOmniSignDocumentsContext();
  const isDesktop = useIsDesktop();

  const signed = useWatch({ name: page });
  const disabled = Boolean(!signed?.value);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (isDesktop || !scrollRef.current) return;
    const element = scrollRef.current;
    element.scrollIntoView();
  }, [scrollRef, isDesktop]);

  const onClick = useCallback(() => {
    if (disabled || page === 'directDebit') return;
    if (page === 'preContact') {
      setPage('creditAgreement');
      setTimeout(() => scrollToTop(), 10);
      return;
    }
    setPage('directDebit');
    setTimeout(() => scrollToTop(), 10);
  }, [disabled, page, setPage, scrollToTop]);

  const tickboxProps = useMemo(() => {
    const fieldText = FIELD_TEXT[page];
    return {
      name: page,
      fieldText,
      ...TICKBOX_PROPS,
    };
  }, [page]);

  const buttonProps = useMemo(() => {
    const submit = page === 'directDebit';
    const label =
      page === 'directDebit' ? 'Accept, Sign & Submit' : 'Accept & Sign';
    return {
      submit,
      id: `${page}-sign-document`,
      label,
      onClick,
      disabled,
      ...BUTTON_PROPS,
    };
  }, [page, disabled, onClick]);

  return {
    tickboxProps,
    buttonProps,
  };
}
